import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';

import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import {
  merge,
  UndefinedValueMergeBehavior
} from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import React, { useContext, useEffect } from 'react';
import { useClassNameMapper } from 'react-bootstrap';

import { ItemType, MessageTimestamp, MessageViewVariant } from '../../../types/enums';
import type {
  MessageViewFragment,
  RelatedContentQuery,
  RelatedContentQueryVariables
} from '../../../types/graphql-types';
import PaneledItemList from '../../common/List/PaneledItemList/PaneledItemList';
import EditableWidget from '../../common/Widget/EditableWidget';
import type { WidgetFC } from '../../common/Widget/types';
import type { ItemViewTypeAndProps } from '../../entities/types';

import useEntityViewQuery from '../../useEntityViewQuery';
import useTranslation from '../../useTranslation';
import relatedContentQuery from './RelatedContent.query.graphql';
import type { RelatedContentWidgetProps } from './types';

const defaultProps: RelatedContentWidgetProps = {
  style: 'compact',
  listVariant: {
    type: ListVariant.UNSTYLED
  },
  pagerVariant: {
    type: PagerVariant.LOAD_MORE
  },
  viewVariant: {
    type: MessageViewVariant.INLINE,
    props: {
      useBoardIcon: true,
      useBody: false,
      useCompactSpacing: true,
      useNode: false,
      timeStampType: MessageTimestamp.POST_TIME,
      renderPostTimeBeforeAuthor: true,
      useTags: false,
      useMedia: false,
      useKudosCount: false,
      useRepliesCount: false,
      useAvatar: false,
      clampSubjectLines: 1,
      portraitClampBodyLines: 3,
      useAuthorLogin: true,
      useAuthorLoginLink: true,
      useSolvedBadge: false,
      boardIconSize: IconSize.PX_24,
      useVideoPreview: false
    }
  },
  pageSize: 5,
  useTitle: true,
  hideIfEmpty: true,
  panelType: PanelType.STANDARD,
  lazyLoad: false,
  enablePagination: false
};

export function getFinalProps(props: RelatedContentWidgetProps): RelatedContentWidgetProps {
  return merge(defaultProps, props, {
    undefinedMergeBehavior: UndefinedValueMergeBehavior.IGNORE_BEFORE_MERGE,
    mergeNested: false
  });
}
/**
 * A widget to display the list of messages whose content is related to the currently viewed post.
 *
 * @author Manasvini Arul
 */
const RelatedContentWidget: WidgetFC<RelatedContentWidgetProps> = props => {
  const { isVisible, ...rest } = props;
  const finalProps: RelatedContentWidgetProps = getFinalProps(rest);
  const {
    listVariant,
    pagerVariant,
    viewVariant,
    className,
    pageSize,
    useTitle,
    hideIfEmpty,
    panelType,
    enablePagination
  } = finalProps;

  const cx = useClassNameMapper();
  const i18n = useTranslation(EndUserComponent.RELATED_CONTENT_WIDGET);
  const { formatMessage, loading: textLoading } = i18n;
  const { contextMessage } = useContext(AppContext);

  const title = formatMessage('title');
  const useEmpty = !hideIfEmpty;

  const queryResult = useEntityViewQuery<
    ItemType.MESSAGE,
    RelatedContentQuery,
    RelatedContentQueryVariables
  >(module, ItemType.MESSAGE, viewVariant, relatedContentQuery, {
    variables: {
      postId: contextMessage.uid,
      first: pageSize
    }
  });

  const { data, loading } = queryResult;

  useEffect(() => {
    if (!useEmpty && !loading) {
      isVisible(data?.relatedContent?.edges?.length !== 0);
    }
  }, [data, isVisible, loading, useEmpty]);

  if (textLoading) {
    return null;
  }

  /**
   * Function to render widget header
   */
  function renderHeader(): React.ReactElement {
    return <h5 className={cx('mb-0')}>{title}</h5>;
  }

  /**
   * Function to render Empty State when there is no related content
   */
  function renderEmptyState(): React.ReactElement {
    return (
      <EmptyState alignment={TextAlignment.LEFT} description={formatMessage('emptyDescription')} />
    );
  }

  return (
    <EditableWidget<RelatedContentWidgetProps> props={finalProps}>
      <PaneledItemList<
        MessageViewFragment,
        ItemType.MESSAGE,
        ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant>,
        RelatedContentQuery,
        RelatedContentQueryVariables
      >
        type={ItemType.MESSAGE}
        variant={viewVariant}
        listVariant={listVariant}
        queryResult={queryResult}
        querySubObject="relatedMessage"
        panel={panelType}
        pagerVariant={pagerVariant}
        pageSize={pageSize}
        header={useTitle && renderHeader}
        bodyClassName="lia-g-mb-0"
        className={cx(className)}
        useEmpty={useEmpty}
        empty={renderEmptyState}
        useFooter={enablePagination && pagerVariant.type !== PagerVariant.NONE}
        id="RelatedContentWidget"
      />
    </EditableWidget>
  );
};

export default RelatedContentWidget;
